import React, { useCallback, useEffect, useRef, useState } from 'react'
import SavedSKUGroupCommonTable from './SavedSKUGroupCommonTable'
import SKUGroupCommonSelector from '../SKUGroupCommonSelector'
import { getEntityPriority } from '../../../services/services';
import { ALL_COLUMNS_OF_PRODUCT_SLAB_ALL, INVALID, INVALID_ALL_COLUMN_ALL_IN_ILC, INVALID_BATCHCODE_OVERLAPPING, labelMappings } from '../../../utils/utils';
import { openPopup } from '../../../../../stateManagement/actions/popupActions';
import { deleteALLAndAddNewSchemeProductBifurcationsList, deleteSchemeProductBifurcationsList, resetSlabInfo, resetMustBuy, updateSchemeProductBifurcationsList } from '../../../../../stateManagement/actions/manageCouponActions';
import { Button } from '@mui/material';
import { ConfirmationPopUp } from '../../../../../components/popup/confirmationPopUp';
import { AddCircleOutlineOutlined } from '@material-ui/icons';
import { filterSelectedProductGroup, getHeadersList, getRowData, getUniqueValuesfromAllProducts, validateSchemeProductBifurcationsList } from './service';
import { isMustBuyEmpty } from '../mustBuy/service';
import { useCouponAlert } from '../../../context/CouponAlert';
import { useSelector } from 'react-redux';


let INITIAL_STATE: Record<string, any> = { id: null }

/**
 * This componet combines the SKUGroupSelector and SKUGroupSaved table
 * @returns 
 */
function SKUGroupWithTable() {
    const [currentSKUGroup, setCurrentSKUGroup] = useState<Record<string,any>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [tabsList, setTabList] = useState<string[]>([]);
    const [selectorVisible, setSelectorVisible] = useState<boolean>(false);
    const [confirmationDetails , setConfirmationDetails] = useState<{message:string,open:boolean}>({message:'',open:false})
    const { schemeProductBifurcationsList } = useSelector((state: any) => state?.manageCoupons?.data)

    const [saveLoading , setSaveLoading] = useState<boolean>(false);
    const successMethodRef = useRef(()=>{});

    const {showAlert} = useCouponAlert();

    const editRef = useRef<number | null>(null);

    const handleSave = async (): Promise<void> => {
        setSaveLoading(true);
        const filteredSKUGrp = await filterSelectedProductGroup(currentSKUGroup,tabsList);
        const response = await validateSchemeProductBifurcationsList(filteredSKUGrp,tabsList);
        
        setSaveLoading(false);
        if (response === ALL_COLUMNS_OF_PRODUCT_SLAB_ALL) {
            setConfirmationDetails(()=>({message:labelMappings["ALL_SELECTED_IN_PRODUCT_SELECTION"],open:true}))
            successMethodRef.current=()=>{
                deleteALLAndAddNewSchemeProductBifurcationsList(currentSKUGroup);
                showAlert(`Data ${editRef.current === null ? 'Saved' :'Updated' }`,'success')
                resetState();
            }
            return;
        }
        else if(response === INVALID_BATCHCODE_OVERLAPPING){
            openPopup("Alert", labelMappings["PRODUCT_SELECTION_BATCHCODE_OVERLAPPING"])
            return;
        }
        else if (response === INVALID) {
            openPopup("Alert", labelMappings["couponAlreadyPresent"])
            return;
        }
        else if(response === INVALID_ALL_COLUMN_ALL_IN_ILC){
            openPopup("Alert", labelMappings["ALL_SELECTION_INVALID_IN_ILC"])
            return;
        }

        updateSchemeProductBifurcationsList(filteredSKUGrp, currentSKUGroup.id);
        showAlert(`Data ${editRef.current === null ? 'Saved' :'Updated' }`,'success')
        resetState();
    }

    const resetState =()=>{
        setCurrentSKUGroup(INITIAL_STATE);
        setSelectorVisible(false);
        editRef.current = null;
    }

    const handleChange = async (selectedItems: string[], entityName: string) => {
        // get filtered data after removing lowerPriority data
        const currentEntityPriority = getEntityPriority(entityName);
        let filteredData:Record<string,any> = {...currentSKUGroup};
        let isLowerPriorityRemoved = false;
        tabsList.forEach((tab)=>{
            // higher value means lower priority
            if(getEntityPriority(tab) > currentEntityPriority && filteredData[tab]?.length>0){
                filteredData[tab] = [];
                isLowerPriorityRemoved = true;
            }else{
                filteredData[tab] = currentSKUGroup[tab];
            }
        })

        if(isLowerPriorityRemoved){
            // showing appropriate conformation message
            const msg = labelMappings["TABS_LOWER_PRIORITY_DATA_RESET"]
            successMethodRef.current = ()=>{
                setCurrentSKUGroup(() => ({ ...filteredData, [entityName]: selectedItems?.length > 0 ? selectedItems : [] }))
            }
            setConfirmationDetails(()=>({message:msg,open:true}))            
        }
        else setCurrentSKUGroup(() => ({ ...filteredData, [entityName]: selectedItems?.length > 0 ? selectedItems : [] }))

    }

    const handleEditButtonClick = (rowIndex: number): void => {
        if(editRef.current !== null){
            openPopup("Alert",labelMappings["already-editing-another-row"]);
            return;
        }
     
        if(!isMustBuyEmpty()){
            // reset must buy
            successMethodRef.current = () => {
                editOperations(rowIndex)
                resetMustBuy();
                resetSlabInfo();
            }
            setConfirmationDetails(()=>({message:labelMappings["RESET_DEPENDENT_DATA"],open:true}))
        }else{
           editOperations(rowIndex);
        }
    }

    const editOperations = (rowIndex:number)=>{
        editRef.current = rowIndex;
        const reformattedData: any = getRowData(rowIndex);
        setSelectorVisible(true);
        deleteSchemeProductBifurcationsList(rowIndex);
        setCurrentSKUGroup(reformattedData);
    }

    const handleDelete = (rowIndex: number): void => {
        if(editRef.current !== null){
            openPopup("Alert",labelMappings["cannot-delete-while-editing"]);
            return;
        }

        if(!isMustBuyEmpty()){
            // reset must buy
            successMethodRef.current = () => {
                deleteSchemeProductBifurcationsList(rowIndex);
                resetMustBuy();
                resetSlabInfo();
            }
            setConfirmationDetails(()=>({message:labelMappings["RESET_DEPENDENT_DATA"],open:true}))
        }else{
            deleteSchemeProductBifurcationsList(rowIndex);
        }
    }

    const getTabList = useCallback(async () => {
        setLoading(true);
        try {
            const tablist = await getHeadersList();
            setTabList(tablist);
            tablist.forEach((item: string) => {
                INITIAL_STATE[item] = []
            })
            setCurrentSKUGroup(INITIAL_STATE)
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [])

    useEffect(() => {
        getTabList();
    }, [])

    useEffect(()=>{
        if(schemeProductBifurcationsList.length === 0){
            setSelectorVisible(true);
        } 
    },[schemeProductBifurcationsList])

    return (
        <div>
            <ConfirmationPopUp
                message={confirmationDetails.message}
                openConfirmModal={confirmationDetails.open}
                setOpenConfirmModal={(val)=>setConfirmationDetails(()=>({message:"",open:val as boolean}))}
                successMethod={successMethodRef.current}
            />

            <div className="sku-group-with-table-header" style={{display:'flex',justifyContent:'flex-end'}}>
                {!selectorVisible && <Button onClick={()=>setSelectorVisible(true)} className='sku-group-addmore-btn' size='small' startIcon={<AddCircleOutlineOutlined />}>Add More</Button>}
            </div>
            <div className="sku-group-with-table-content" style={{ display: 'flex', flexDirection: 'column', gap: '10px' ,marginTop:'5px'}}>
                {
                    selectorVisible && <SKUGroupCommonSelector 
                        getTabContent={getUniqueValuesfromAllProducts}
                        editMode={editRef.current !== null}
                        handleChange={handleChange}
                        tabsList={tabsList}
                        handleSave={handleSave}
                        data={currentSKUGroup} 
                        loading={loading} 
                        multiple={true}
                        saveLoading={saveLoading}
                    />
                }
                <SavedSKUGroupCommonTable
                    handleRowEdit={handleEditButtonClick} 
                    handleRowDelete={handleDelete} 
                />
            </div>
        </div>
    )
}

export default React.memo(SKUGroupWithTable);